import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { formatCalendarDate } from '@twaice-fe/frontend/shared/utilities';
import {
  AggregationLevelBkParams,
  AggregationLevelBkResponse,
  DataNotFoundError,
  PerformanceManagerResponseInterface,
  SeverityLevelParams,
  StringKPIParams,
  StringKPIResponse,
  StringThroughputParams,
  StringThroughputResponse,
  SystemInsightsParams,
  SystemInsightsResponse,
  SystemKPIParams,
  SystemKPIResponse,
  SystemSeverityResponse,
  SystemStatisticsParams,
  SystemStatisticsResponse,
} from '@twaice-fe/shared/models';
import { keysToCamel } from '@twaice-fe/shared/utilities';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PerformanceManagerService {
  private storageTimezone = 'UTC';

  constructor(
    private httpClient: HttpClient,
    @Optional() private store?: Store
  ) {
    if (!this.store) return;

    this.store.select(selectors.systemSelectors.getSelected).subscribe((system) => {
      this.storageTimezone = system?.bessMetadata?.storageTimezone ?? 'UTC';
    });
  }

  fetchSystemStatistics(params: SystemStatisticsParams): Observable<SystemStatisticsResponse> {
    const url = this.buildUrl('analytics/performance/statistics', [
      'customers',
      params.customerBk,
      'systems',
      params.systemBk,
      params.kpi,
    ]);

    let httpParams = new HttpParams()
      .set('aggregation_level_bk', params.aggregationLevelBk)
      .set('startDate', this.timestampToDateStr(params.startDate))
      .set('endDate', this.timestampToDateStr(params.endDate));
    httpParams = httpParams.set('kpiVersion', 2);

    return this.fetchData<SystemStatisticsResponse>(url, httpParams);
  }

  fetchSystemKPIData(params: SystemKPIParams): Observable<SystemKPIResponse> {
    const url = this.buildUrl('analytics/performance/data', [
      'customers',
      params.customerBk,
      'systems',
      params.systemBk,
      'levels',
      params.levelBk,
      params.kpi,
    ]);

    // The selected day is a calendar date pinned to midnight UTC, so re-localising it would land on the previous day.
    let httpParams = new HttpParams().set('date', this.dayToDateStr(params.date));
    httpParams = httpParams.set('kpiVersion', 2);

    return this.fetchData<SystemKPIResponse>(url, httpParams).pipe(
      map((systemData) => ({
        ...systemData,
        data: systemData.data.map((d) =>
          // @ts-expect-error  API responds with event dates in iso format
          ({ ...d, eventDate: this.dateStrToTimestamp(d.eventDate) })
        ),
      }))
    );
  }

  fetchSystemInsights(params: SystemInsightsParams): Observable<SystemInsightsResponse> {
    const url = this.buildUrl('analytics/performance/insights', [
      'customers',
      params.customerBk,
      'systems',
      params.systemBk,
      params.kpi,
      this.dayToDateStr(params.date),
    ]);

    return this.httpClient.get<PerformanceManagerResponseInterface<SystemInsightsResponse>>(url).pipe(map(({ data }) => data));
  }

  fetchSystemSeverityLevel(params: SeverityLevelParams): Observable<SystemSeverityResponse> {
    const url = this.buildUrl('analytics/performance/configuration', [
      'customers',
      params.customerBk,
      'systems',
      params.systemBk,
      'severity',
    ]);
    return this.httpClient.get<PerformanceManagerResponseInterface<SystemSeverityResponse>>(url).pipe(map(({ data }) => data));
  }

  fetchAggregationLevel(params: AggregationLevelBkParams): Observable<AggregationLevelBkResponse> {
    const url = this.buildUrl('analytics/performance/configuration', [
      'customers',
      params.customerBk,
      'systems',
      params.systemBk,
      'aggregation-level',
    ]);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('kpiVersion', 2);

    return this.httpClient
      .get<PerformanceManagerResponseInterface<AggregationLevelBkResponse>>(url, { params: httpParams })
      .pipe(map(({ data }) => data));
  }

  fetchStringKPIData(params: StringKPIParams): Observable<StringKPIResponse> {
    const url = this.buildUrl('analytics/performance/data', [
      'customers',
      params.customerBk,
      'systems',
      params.systemBk,
      'components',
      params.parentComponentBk,
      'levels',
      'string',
      params.kpi,
    ]);

    let httpParams = new HttpParams()
      .set('startDate', this.timestampToDateStr(params.startDate))
      .set('endDate', this.timestampToDateStr(params.endDate));
    httpParams = httpParams.set('kpiVersion', 2);

    return this.fetchData<StringKPIResponse>(url, httpParams).pipe(
      map((systemData) => ({
        ...systemData,
        data: systemData.data.map((d) => ({
          ...d,
          eventDate: new Date(`${d.eventDate}T00:00:00Z`).getTime() / 1000,
        })),
      }))
    );
  }

  fetchStringEfcData(customerBk: string, systemBk: string, params: StringThroughputParams): Observable<StringThroughputResponse> {
    const url = `analytics/performance/throughput/customers/${customerBk}/systems/${systemBk}/levels/string/efc`;
    const secondsInDay = 24 * 60 * 60;
    const startDateMidnight = Math.floor(params.startDate / secondsInDay) * secondsInDay;
    const endDateMidnight = Math.floor(params.endDate / secondsInDay) * secondsInDay;

    let httpParams = new HttpParams().set('date_from', startDateMidnight).set('date_to', endDateMidnight);

    if (params.parentComponentBk) {
      httpParams = httpParams.set('parent_component_bk', params.parentComponentBk);
    }

    const rawResponse = this.fetchData<StringThroughputResponse>(url, httpParams);

    return rawResponse.pipe(map((data) => keysToCamel(data)));
  }

  private fetchData<T>(url: string, params?: HttpParams): Observable<T> {
    return this.httpClient.get<PerformanceManagerResponseInterface<T>>(url, { params }).pipe(
      map(({ data }) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!(data as any)?.data?.length) {
          throw new DataNotFoundError(`No data found for the given parameters.`);
        }
        return data;
      }),
      catchError((error: Error) => {
        console.error(`Error occurred while fetching data from ${url} with params: ${params?.toString()}`, error.message);
        return throwError(() => error);
      })
    );
  }

  private buildUrl(endpoint: string, params: (string | number)[]): string {
    return `${endpoint}/${params.join('/')}`;
  }

  private timestampToDateStr(ts: number): string {
    return formatCalendarDate(new Date(ts * 1000), this.storageTimezone);
  }

  private dayToDateStr(day: number): string {
    return new Date(day * 1000).toISOString().split('T')[0];
  }

  private dateStrToTimestamp(dateStr: string): number {
    return new Date(dateStr).getTime() / 1000;
  }
}
